import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { EtatExpedition } from '../components/notifications/BandeauExpedition';

/**
 * Préinscriptions en ligne (phase 5, TF-429). Le candidat dépose sans compte
 * par le formulaire public ; la direction valide — le dossier `INSCRIT` naît —
 * ou refuse avec un motif. Doublons, refus et messages viennent de l'API
 * (`domain/preinscriptions.ts`) : les écrans affichent, ils ne décident rien.
 */

export type ModeDossier = 'CFA' | 'OF';
export type StatutPreinscription = 'EN_ATTENTE' | 'VALIDEE' | 'REFUSEE';
export type CanalPrevenu = 'EMAIL' | 'SMS' | 'IN_APP';

// --- Formulaire public ------------------------------------------------------

export interface FormulairePreinscription {
  organisme: string;
  antennes: { code: string; nom: string; ville: string | null }[];
  formations: { code: string; titre: string }[];
  parcours: { valeur: ModeDossier; libelle: string }[];
}

export interface DepotPreinscription {
  nom: string;
  prenom: string;
  dateNaissance: string;
  email: string;
  telephone: string;
  mode: ModeDossier | '';
  siteCode: string;
  formationCode: string;
  message: string;
  consentement: boolean;
  /** Champ piège, laissé vide par une personne. */
  siteWeb: string;
}

export function useFormulairePreinscription() {
  return useQuery({
    queryKey: ['preinscriptions', 'formulaire'],
    queryFn: () => appelApi<FormulairePreinscription>('/preinscriptions/formulaire'),
    retry: false,
    staleTime: 10 * 60 * 1000,
  });
}

export function useDeposerPreinscription() {
  return useMutation({
    mutationFn: (depot: DepotPreinscription) =>
      appelApi<{ message: string }>('/preinscriptions/depot', { methode: 'POST', corps: depot }),
  });
}

// --- File de la direction ---------------------------------------------------

export interface DoublonPossible {
  type: 'DOSSIER' | 'PREINSCRIPTION';
  id: string;
  nom: string;
  detail: string;
  motifs: string[];
  lien: string | null;
}

export interface LignePreinscription {
  id: string;
  nom: string;
  prenom: string;
  dateNaissance: string;
  age: number;
  email: string;
  telephone: string;
  mode: ModeDossier;
  parcours: string;
  site: { code: string; nom: string };
  formation: { code: string; titre: string } | null;
  message: string | null;
  deposeeLe: string;
  statut: StatutPreinscription;
  statutLibelle: string;
  decision: { le: string; par: string | null; motif: string | null } | null;
  dossier: { id: string; statutLibelle: string; statutTon: string } | null;
  prevenu: { le: string; canaux: CanalPrevenu[] } | null;
  /** Canaux par lesquels la décision partirait ; vide sans prestataire raccordé. */
  canauxDecision: CanalPrevenu[];
  doublons: DoublonPossible[];
  refusDecision: string | null;
  refusRenvoi: string | null;
}

export interface ReponsePreinscriptions {
  calculeLe: string;
  lienFormulaire: string;
  expedition: EtatExpedition;
  compteurs: {
    aTraiter: number;
    doublons: number;
    plusAncienneJours: number | null;
    validees: number;
    refusees: number;
  };
  aTraiter: LignePreinscription[];
  historique: LignePreinscription[];
  options: {
    antennes: { code: string; nom: string }[];
    formations: { code: string; titre: string }[];
    parcours: { valeur: ModeDossier; libelle: string }[];
    motifsProposes: string[];
  };
}

export interface ResultatDecision {
  preinscription: LignePreinscription;
  message: string;
}

const CLE = ['preinscriptions', 'file'];

export function usePreinscriptions(site: string) {
  return useQuery({
    queryKey: [...CLE, site],
    queryFn: () => appelApi<ReponsePreinscriptions>(`/preinscriptions?site=${encodeURIComponent(site)}`),
    placeholderData: keepPreviousData,
  });
}

function useApresDecision() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: CLE });
    // Le pipeline du tableau de bord compte les préinscriptions à traiter.
    void client.invalidateQueries({ queryKey: ['financements', 'tableau-de-bord-direction'] });
    // La validation crée un dossier : les listes de candidats le montrent.
    void client.invalidateQueries({ queryKey: ['apprenants'] });
  };
}

export interface CorrectionsValidation {
  siteCode: string;
  mode: ModeDossier;
  formationCode: string | null;
}

export function useValiderPreinscription() {
  const apres = useApresDecision();
  return useMutation({
    mutationFn: ({ id, corrections }: { id: string; corrections: CorrectionsValidation }) =>
      appelApi<ResultatDecision>(`/preinscriptions/${id}/validation`, { methode: 'POST', corps: corrections }),
    onSuccess: apres,
  });
}

export function useRefuserPreinscription() {
  const apres = useApresDecision();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) =>
      appelApi<ResultatDecision>(`/preinscriptions/${id}/refus`, { methode: 'POST', corps: { motif } }),
    onSuccess: apres,
  });
}

export function useRenvoyerDecision() {
  const apres = useApresDecision();
  return useMutation({
    mutationFn: (id: string) => appelApi<ResultatDecision>(`/preinscriptions/${id}/notification`, { methode: 'POST' }),
    onSuccess: apres,
  });
}
